import { brandIcon, icon } from '../../icons';
import { t } from '../../services/i18n';
import { clearModalPortal, renderModalPortal } from '../shared/modal-portal';

export class RcLoginView extends HTMLElement {
  loading = false;
  error = '';
  errorCode = '';
  success = '';
  providersReady = false;
  emailEnabled = false;
  emailExpanded = false;
  emailInput = '';
  googleEnabled = false;
  microsoftEnabled = false;
  providerError = '';
  loggedOutNotice = '';
  signupNotificationEmail = '';
  signupNotificationSubmitting = false;
  signupNotificationError = '';
  signupNotificationSuccess = '';
  signupNotificationSubmitted = false;

  connectedCallback(): void {
    renderModalPortal(this, '').addEventListener('click', this.handlePortalClick);
  }

  disconnectedCallback(): void {
    renderModalPortal(this, '').removeEventListener('click', this.handlePortalClick);
    clearModalPortal(this);
  }

  private escape(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  render(): void {
    const signupNotificationMarkup = this.errorCode === 'signup_disabled'
      ? `<div class="login-notification-form">
          <p class="login-notification-form-copy">${t('auth.signupNotification.body')}</p>
          <label class="login-notification-form-label" for="signup-notification-email">${t('auth.signupNotification.emailLabel')}</label>
          <input
            id="signup-notification-email"
            class="login-notification-input"
            type="email"
            value="${this.escape(this.signupNotificationEmail)}"
            placeholder="${this.escape(t('auth.signupNotification.emailPlaceholder'))}"
            disabled
            readonly
          >
          ${this.signupNotificationSubmitted
            ? `<div class="login-notification-success" role="status">
                <div class="login-notification-success-icon">${icon('check-circle', { size: 20 })}</div>
                <div class="login-notification-success-copy">
                  <strong>${t('auth.signupNotification.successTitle')}</strong>
                  <p>${this.escape(this.signupNotificationSuccess)}</p>
                </div>
              </div>`
            : `<div class="login-notification-form-row">
                <button class="login-btn login-notification-submit" type="button" ${this.signupNotificationSubmitting ? 'disabled' : ''}>
                  <span class="login-btn-label">${this.signupNotificationSubmitting ? t('common.loading') : t('auth.signupNotification.submit')}</span>
                </button>
              </div>`}
          ${this.signupNotificationError ? `<div class="form-error">${this.escape(this.signupNotificationError)}</div>` : ''}
        </div>`
      : '';

    const signupDisabledModalMarkup = this.error && this.errorCode === 'signup_disabled'
      ? `<div class="modal-overlay login-alert-modal" role="dialog" aria-modal="true" aria-labelledby="login-alert-title">
          <div class="modal login-alert login-alert-popup" role="alert">
            <button type="button" class="modal-close login-alert-close" aria-label="${this.escape(t('common.close'))}">
              ${icon('x', { size: 18 })}
            </button>
            <div class="login-alert-icon">${icon('alert-circle', { size: 18 })}</div>
            <div class="login-alert-copy">
              <strong id="login-alert-title">${t('auth.errorTitle')}</strong>
              <p>${this.escape(this.error)}</p>
              ${signupNotificationMarkup}
            </div>
          </div>
        </div>`
      : '';

    const loggedOutNoticeModalMarkup = this.loggedOutNotice
      ? `<div class="modal-overlay login-alert-modal" role="dialog" aria-modal="true" aria-labelledby="login-alert-title">
          <div class="modal login-alert login-alert-popup" role="alert">
            <button type="button" class="modal-close login-alert-close" aria-label="${this.escape(t('common.close'))}">
              ${icon('x', { size: 18 })}
            </button>
            <div class="login-alert-icon">${icon('alert-circle', { size: 18 })}</div>
            <div class="login-alert-copy">
              <strong id="login-alert-title">${t('auth.errorTitle')}</strong>
              <p>${this.escape(this.loggedOutNotice)}</p>
            </div>
          </div>
        </div>`
      : '';

    const errorMarkup = this.error
      ? this.errorCode === 'signup_disabled'
        ? ''
        : `<div class="login-alert" role="alert">
            <div class="login-alert-icon">${icon('alert-circle', { size: 18 })}</div>
            <div class="login-alert-copy">
              <strong>${t('auth.errorTitle')}</strong>
              <p>${this.escape(this.error)}</p>
            </div>
          </div>`
      : '';

    const successMarkup = this.success
      ? `<div class="login-alert login-alert-success" role="status">
          <div class="login-alert-icon">${icon('check-circle', { size: 18 })}</div>
          <div class="login-alert-copy">
            <strong>${t('auth.successTitle')}</strong>
            <p>${this.escape(this.success)}</p>
          </div>
        </div>`
      : '';

    const emailMarkup = this.emailEnabled
      ? `<div class="login-provider-group${this.emailExpanded ? ' is-expanded' : ''}">
          <button class="login-btn login-btn-email" type="button" aria-expanded="${this.emailExpanded ? 'true' : 'false'}">
            <span class="login-provider-leading">${icon('mail', { size: 18, className: 'login-btn-icon' })}</span>
            <span class="login-btn-label">${t('auth.emailProvider')}</span>
            <span class="login-provider-trailing">${icon(this.emailExpanded ? 'x' : 'chevron-right', { size: 16 })}</span>
          </button>
          ${this.emailExpanded
            ? `<form class="login-email-form">
                <label class="login-email-label" for="login-email-input">${t('auth.emailLabel')}</label>
                <input
                  id="login-email-input"
                  class="login-notification-input login-email-input"
                  type="email"
                  value="${this.escape(this.emailInput)}"
                  placeholder="${this.escape(t('auth.emailPlaceholder'))}"
                  ${this.loading ? 'disabled' : ''}
                  required
                >
                <button class="login-btn login-email-submit" type="submit" ${this.loading ? 'disabled' : ''}>
                  <span class="login-email-submit-icon">${icon('mail', { size: 18, className: 'login-btn-icon' })}</span>
                  <span class="login-btn-label">${t('auth.loginWithEmail')}</span>
                </button>
              </form>`
            : ''}
        </div>`
      : '';

    const enabledButtons = [
      emailMarkup,
      this.googleEnabled
        ? `<button class="login-btn login-btn-google">${brandIcon('google', { size: 20, className: 'login-btn-icon' })}<span class="login-btn-label">${t('auth.loginWithGoogle')}</span></button>`
        : '',
      this.microsoftEnabled
        ? `<button class="login-btn login-btn-microsoft">${brandIcon('microsoft', { size: 20, className: 'login-btn-icon' })}<span class="login-btn-label">${t('auth.loginWithMicrosoft')}</span></button>`
        : '',
    ].join('');

    const providerMarkup = !this.providersReady
      ? `<div class="login-global-spinner"><span class="spinner"></span><span class="login-global-spinner-label">${t('common.loading')}</span></div>`
      : this.providerError
        ? `<div class="login-alert" role="alert">
            <div class="login-alert-icon">${icon('alert-circle', { size: 18 })}</div>
            <div class="login-alert-copy">
              <strong>${t('auth.errorTitle')}</strong>
              <p>${this.escape(this.providerError)}</p>
            </div>
          </div>`
        : enabledButtons
          ? `<div class="login-btn-stack">${enabledButtons}</div>`
          : `<div class="login-alert" role="status">
              <div class="login-alert-icon">${icon('alert-circle', { size: 18 })}</div>
              <div class="login-alert-copy">
                <strong>${t('auth.errorTitle')}</strong>
                <p>${t('auth.noLoginProviders')}</p>
              </div>
            </div>`;

    this.innerHTML = this.loading
      ? `${errorMarkup}${successMarkup}<div class="login-global-spinner"><span class="spinner"></span><span class="login-global-spinner-label">${t('auth.loggingIn')}</span></div>`
      : `${errorMarkup}${successMarkup}${providerMarkup}`;

    renderModalPortal(this, signupDisabledModalMarkup || loggedOutNoticeModalMarkup);

    if (!this.loading && this.providersReady) {
      this.querySelector('.login-email-form')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.dispatchEvent(new CustomEvent('login-email', { bubbles: true }));
      });
      this.querySelector('.login-btn-email')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('login-email-toggle', { bubbles: true })));
      this.querySelector('.login-btn-google')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('login-google', { bubbles: true })));
      this.querySelector('.login-btn-microsoft')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('login-microsoft', { bubbles: true })));
    }
  }

  private handlePortalClick = (event: Event): void => {
    const rawTarget = event.target as HTMLElement | null;
    const closeTarget = rawTarget?.closest<HTMLElement>('.login-alert-close');
    if (closeTarget || rawTarget?.classList.contains('login-alert-modal')) {
      this.dispatchEvent(new CustomEvent('login-dismiss-error', { bubbles: true }));
      return;
    }

    const submitTarget = rawTarget?.closest<HTMLElement>('.login-notification-submit');
    if (!submitTarget) {
      return;
    }

    this.dispatchEvent(new CustomEvent('signup-notification-submit', { bubbles: true }));
  };
}

if (!customElements.get('rc-login-view')) {
  customElements.define('rc-login-view', RcLoginView);
}
